import { warn } from "@/lib/log";
// Local-first Household Emergency Plan store (v2).
// All plan data stays on the device. Nothing is sent to any server.
const KEY = "ukready_plan_v2";
const LEGACY = "ukready_plan";

const read = (k, fb) => { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const write = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { warn("Could not save to this device (storage full or blocked)", e); } };

export function emptyPlan() {
  return {
    _v: 2, onboarded: false, basicComplete: false,
    created: new Date().toISOString(), updated: new Date().toISOString(),
    lastReviewed: null, planVersion: 1,
    household: { postcode: "", nation: "England", homeType: "House", adults: 2, children: 0, babies: 0, older: 0, hasPets: "no", dependencies: [], attends: [], vehicle: "yes" },
    people: [], childrenList: [],
    contacts: { outOfArea: { name: "", relationship: "", mobile: "", landline: "", area: "", alt: "" }, tree: [] },
    peopleAround: [], responsibilities: [],
    meetingPoints: { a: { place: "", notes: "" }, b: { place: "", notes: "" }, c: { place: "", notes: "" } },
    home: { electricity: "", gas: "", water: "", smoke: "", co: "", extinguisher: "", exits: "", escape: { normal: "", alternative: "", upstairs: "", accessible: "", keys: "", assembly: "" } },
    utilities: { dno: "", dnoNumber: "", water: "", waterNumber: "", council: "", councilNumber: "", psr: { electricity: false, gas: false, water: false } },
    medical: { enabled: false, showInPrint: false, meds: [] },
    power: { enabled: false, items: [] },
    pets: [],
    kit: {}, kitChecked: null,
    documents: {}, insurance: { insurer: "", policy: "", claims: "", assistance: "" },
    vehicles: [],
    evacuation: { checklist: {}, destinationPrimary: "", destinationSecondary: "", transport: "", route: "", backupRoute: "" },
    goBag: { items: {}, location: "" },
    stayHome: {},
    commsDown: { paperListPrinted: "no", radios: [] },
    locations: { workplace: "", school: "", nursery: "", college: "", care: "" },
  };
}

function migrateLegacy(p) {
  const old = read(LEGACY, null);
  if (!old) return p;
  p.household.postcode = old.postcode || "";
  p.household.adults = old.adults ?? 2;
  p.household.children = old.children ?? 0;
  p.household.babies = old.babies ?? 0;
  p.household.older = old.older ?? 0;
  p.household.homeType = old.homeType || "House";
  p.household.hasPets = old.pets ? "yes" : "no";
  p.household.vehicle = old.vehicle || "yes";
  p.meetingPoints.a.place = old.meeting1 || "";
  p.meetingPoints.b.place = old.meeting2 || "";
  if (old.outOfArea) { const [n, ...rest] = old.outOfArea.split(/[·,-]/); p.contacts.outOfArea.name = (n || "").trim(); p.contacts.outOfArea.mobile = (rest.join(" ") || "").trim(); }
  p.utilities.water = old.water || "";
  p.utilities.council = old.council || "";
  p.insurance.insurer = old.insurance || "";
  return p;
}

export const plan = {
  get() {
    let p = read(KEY, null);
    if (!p) { p = migrateLegacy(emptyPlan()); }
    // ensure new keys exist after schema growth
    return { ...emptyPlan(), ...p, household: { ...emptyPlan().household, ...(p.household || {}) } };
  },
  save(p) {
    p.updated = new Date().toISOString();
    write(KEY, p);
    return p;
  },
  reset() { sessionStorage.removeItem(KEY); },
  bumpVersion() { const p = this.get(); p.planVersion = (p.planVersion || 1) + 1; p.lastReviewed = new Date().toISOString(); return this.save(p); },
  markReviewed() { const p = this.get(); p.lastReviewed = new Date().toISOString(); return this.save(p); },
};

// deep get/set by dot path
export function getPath(obj, path) {
  if (!path) return obj;
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
export function setPath(obj, path, value) {
  const keys = path.split(".");
  const copy = structuredClone(obj);
  let cur = copy;
  for (let i = 0; i < keys.length - 1; i++) { if (cur[keys[i]] == null || typeof cur[keys[i]] !== "object") cur[keys[i]] = {}; cur = cur[keys[i]]; }
  cur[keys[keys.length - 1]] = value;
  return copy;
}

export const NATION_UTILS = {
  England: { power: "105", powerLabel: "Power cut", gas: "0800 111 999", gasLabel: "Gas emergency (National Gas)", floodline: "0345 988 1188" },
  Scotland: { power: "105", powerLabel: "Power cut", gas: "0800 111 999", gasLabel: "Gas emergency (National Gas)", floodline: "0345 988 1188" },
  Wales: { power: "105", powerLabel: "Power cut", gas: "0800 111 999", gasLabel: "Gas emergency (National Gas)", floodline: "0345 988 1188" },
  "Northern Ireland": { power: "03457 643 643", powerLabel: "Power cut (NIE Networks)", gas: "0800 002 001", gasLabel: "Gas emergency (NI)", floodline: "0300 2000 100" },
};
